import React, { useMemo } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { CommandPalette } from "@/components/CommandPalette";
import { HelperWidget } from "@/components/HelperWidget";
import { GlobalHeader } from "@/components/GlobalHeader";
import { Footer } from "@/components/Footer";

/* ---------- Ambient scene: metallic swirl backdrop + drifting embers ---------- */
function BackdropScene() {
  const embers = useMemo(() => {
    const rng = (seed) => { const x = Math.sin(seed) * 10000; return x - Math.floor(x); };
    return Array.from({ length: 34 }, (_, i) => {
      const r1 = rng(i * 3.13 + 1);
      const r2 = rng(i * 7.71 + 2);
      const r3 = rng(i * 11.9 + 3);
      const r4 = rng(i * 17.3 + 4);
      const kind = r4 < 0.18 ? "large" : r4 > 0.78 ? "tiny" : "";
      return {
        i, kind,
        left: `${r1 * 100}%`,
        top: `${55 + r2 * 40}%`,
        delay: `${(r3 * 14).toFixed(2)}s`,
        dur: `${(11 + r2 * 8).toFixed(2)}s`,
        drift: `${(r1 - 0.5) * 100}px`,
      };
    });
  }, []);

  return (
    <div className="wikifi-scene" aria-hidden="true" data-testid="wikifi-scene">
      <div className="wikifi-scene-img" style={{ backgroundImage: "url('/wikifi-bg.jpg')" }} />
      <div className="wikifi-scene-veil" />
      <div className="dune-particles">
        {embers.map((e) => (
          <span
            key={e.i}
            className={`dune-particle ${e.kind}`}
            style={{
              left: e.left,
              top: e.top,
              "--delay": e.delay,
              "--dur": e.dur,
              "--drift": e.drift,
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* ---------- Sidebar categories — 2033 minimal list ---------- */
const CATEGORIES = [
  { key: null,                     label: "All Tools",    to: "/" },
  { key: "AI Agents",              label: "Agents",       to: "/?cat=AI+Agents" },
  { key: "Writing & Copy",         label: "Writing",      to: "/?cat=Writing+%26+Copy" },
  { key: "Coding & Dev",           label: "Coding",       to: "/?cat=Coding+%26+Dev" },
  { key: "Research",               label: "Research",     to: "/?cat=Research" },
  { key: "Creative / Film",        label: "Creative",     to: "/?cat=Creative+%2F+Film" },
  { key: "Marketing",              label: "Marketing",    to: "/?search=marketing" },
  { key: "Productivity",           label: "Productivity", to: "/?cat=Productivity" },
  { key: "Analysis",               label: "Analysis",     to: "/?search=analysis" },
  { key: "Education",              label: "Education",    to: "/?cat=Education" },
  { key: "Finance",                label: "Finance",      to: "/?cat=Finance" },
  { key: "Chat & LLMs",            label: "Assistant",    to: "/?cat=Chat+%26+LLMs" },
];

/* ---------- Titlebar wordmark — clean "Wikificial" ---------- */
function WikifiMark() {
  return (
    <Link to="/" className="inline-flex items-center gap-2" data-testid="home-wikifi-mark">
      <span
        className="font-display font-bold text-[#0B1220] text-[19px] leading-none tracking-tight select-none"
        style={{ letterSpacing: "-0.03em" }}
      >
        Wikificial
      </span>
    </Link>
  );
}

/* ---------- The homepage window shell ---------- */
export function HomeShell({ children, rightPanel, activeCategory, belowWindow, topStrip }) {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const currentCat = activeCategory || params.get("cat");
  // On the homepage the search bar moves inline (below the Popular icon
  // row and above the category pills) so we suppress the sticky Row 2.
  const isHomepage = location.pathname === "/";

  const onPromptSubmit = (e) => {
    e.preventDefault();
    const val = e.currentTarget.query.value.trim();
    if (val) navigate(`/?search=${encodeURIComponent(val)}`);
  };

  const initial = (user?.email?.[0] || "W").toUpperCase();

  return (
    <div className="min-h-screen bg-white text-[#0B1220] relative" data-testid="home-shell">

      {/* Global sticky full-bleed header — used across every page. On the
          homepage we hide Row 2 (search) because it moves inline below the
          Popular tools row. */}
      <GlobalHeader hideSearch={isHomepage} />

      {/* Content shell */}
      <div className="max-w-[1920px] mx-auto px-3 sm:px-5 lg:px-6 pt-2 sm:pt-3 pb-14">
        {/* Full-width strip that sits directly under the header nav and
            spans BOTH the main content column and the right (Breakthroughs)
            sidebar — used by the homepage for the Popular tool icon row. */}
        {topStrip && (
          <div className="mb-4" data-testid="home-top-strip">
            {topStrip}
          </div>
        )}

        {/* --------- 2-column body: content + right sidebar (LeftSidebar is fixed) --------- */}
        <div className="grid grid-cols-12 gap-4 lg:gap-6 min-h-[540px]">
          {/* Center content */}
          <main className="col-span-12 lg:col-span-9 min-w-0 py-2" data-testid="home-content">
            {children}
          </main>

          {/* Live Feed — hidden on mobile for the same reason */}
          <aside className="hidden lg:block lg:col-span-3 py-2" data-testid="home-livefeed-wrap">
            {rightPanel}
          </aside>
        </div>

        {/* Rich content sections that fill space beneath the window */}
        {belowWindow}

        {/* Small footer credit (kept subtle) */}
        <div className="mt-6 text-center text-[11px] text-[#8A93A6]" data-testid="home-footer">
          Wikificial · The Encyclopedia of AI · Always Updating Live
        </div>
      </div>

      <Footer/>

      <CommandPalette />
      <HelperWidget />
    </div>
  );
}
